import type {
  LivePlayer,
  MatchStatus,
  ScoringCategory,
  ScoringEntry
} from './types';

/**
 * Mirror of the engine's `packages/publisher/src/live-objects-channels.ts`.
 * Stats and player channels are scoped under the match namespace so a
 * `match:*` token capability covers them and they expire with the match.
 *
 * Player channels are keyed by Opta id, not FPL id. `/live/team` gives each
 * pick its `optaId`, which is what makes squad-only subscription possible
 * without first reading the match lineup.
 */
export const channelNameForMatch = (matchId: string): string =>
  `match:${matchId}`;

export const channelNameForPlayer = (
  matchId: string,
  playerOptaId: string
): string => `${channelNameForMatch(matchId)}:player:${playerOptaId}`;

export type ChannelRootSnapshot = Record<string, unknown>;

export type MainChannelState = {
  status: MatchStatus;
  kickoffAt: string | null;
  isFinalised: boolean;
  fplSettledAt: string | null;
  /**
   * Optional because it is *omitted* rather than nulled when the root map
   * does not carry the key. See `projectMainChannel`.
   */
  provenance?: string | null;
};

/**
 * Project the match main channel's root LiveMap.
 *
 * Every key is read defensively: the root is empty before the engine
 * publishes, and the engine seeds keys at different lifecycle points
 * (`status` and `kickoffTime` at bootstrap, `isFinalised` only once FPL
 * settlement is quiescent), so an absent key means "not yet", never an error.
 */
const KNOWN_STATUSES: readonly MatchStatus[] = [
  'SCHEDULED',
  'LINEUPS_CONFIRMED',
  'LIVE',
  'HALF_TIME',
  'SECOND_HALF',
  'FULL_TIME',
  'RECONCILING',
  'POSTPONED',
  'ABANDONED'
];

/**
 * Narrow the published status, falling back rather than trusting the wire.
 *
 * A status this client does not know scores 0 in the fixture-priority table,
 * which would quietly rank a live fixture below a scheduled one in a double
 * gameweek. Falling back to SCHEDULED keeps an unrecognised value from
 * outranking anything.
 */
const toMatchStatus = (value: unknown): MatchStatus =>
  KNOWN_STATUSES.includes(value as MatchStatus)
    ? (value as MatchStatus)
    : 'SCHEDULED';

const toStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

export const projectMainChannel = (
  raw: ChannelRootSnapshot
): MainChannelState => {
  const projected: MainChannelState = {
    status: toMatchStatus(raw.status),
    // The engine publishes `kickoffTime`; the pitch's fixture-priority logic
    // has always called it `kickoffAt`.
    kickoffAt: toStringOrNull(raw.kickoffTime),
    isFinalised: raw.isFinalised === true,
    fplSettledAt: toStringOrNull(raw.fplSettledAt)
  };

  // Assigned only when the engine actually published the key, because the
  // caller merges this over the state the REST snapshot already established.
  // Defaulting an absent key to null would read as "this is a real match" and
  // silently unmark a simulated one: against an engine revision that predates
  // provenance, and against any future main-channel slice published without
  // it. Absent has to mean "keep what we had", never "real".
  if ('provenance' in raw) {
    projected.provenance = toStringOrNull(raw.provenance);
  }

  return projected;
};

const SCORING_PREFIX = 'scoring:';
const GENERAL_STATS_PREFIX = 'generalStats:';

const asNumber = (value: unknown): number =>
  typeof value === 'number' ? value : 0;

type PlayerIdentity = {
  optaId: string;
  fplId: string;
  name: string;
  teamId: string;
  position: string;
  shirtNumber: number | null;
};

/**
 * Project a player channel's root LiveMap into the shape the pitch reads.
 *
 * The engine fans scoring categories and general stats out to one key each
 * (`scoring:goals`, `generalStats:totalShots`) so the broker can merge writes
 * from its three publishing sources without them clobbering each other. Put
 * them back into the nested records the tiles already render from.
 *
 * `fplId` comes from the identity slice rather than being passed in, so the
 * value the pitch joins on is the engine's, not one the client assumed.
 */
export const projectPlayerChannel = (
  raw: ChannelRootSnapshot,
  fallbackOptaId: string
): LivePlayer => {
  const scoring: Partial<Record<ScoringCategory, ScoringEntry>> = {};
  const generalStats: Record<string, number> = {};

  for (const [key, value] of Object.entries(raw)) {
    if (key.startsWith(SCORING_PREFIX)) {
      scoring[key.slice(SCORING_PREFIX.length) as ScoringCategory] =
        value as ScoringEntry;
    } else if (key.startsWith(GENERAL_STATS_PREFIX)) {
      generalStats[key.slice(GENERAL_STATS_PREFIX.length)] = asNumber(value);
    }
  }

  const identity = raw.identity as PlayerIdentity | undefined;

  return {
    playerId: identity?.optaId ?? fallbackOptaId,
    fplId: identity?.fplId ?? '',
    isStarter: raw.isStarter === true,
    isOnPitch: raw.isOnPitch === true,
    minutesPlayed: asNumber(raw.minutesPlayed),
    points: asNumber(raw.points),
    bps: asNumber(raw.bps),
    scoring,
    generalStats
  };
};
